import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { ApiError, ApiSchemaError } from "./api/client";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Layout } from "./components/Layout";
import { ToastProvider } from "./components/Toast";
import { TermsGate } from "./components/TermsGate";
import { TokenGate } from "./components/TokenGate";
import { Skeleton } from "./components/ui";
import { TodayPage } from "./pages/Today";

const PlanPage = lazy(() => import("./pages/Plan"));
const ActivitiesPage = lazy(() => import("./pages/Activities"));
const ActivityDetailPage = lazy(() => import("./pages/ActivityDetail"));
const ProgressPage = lazy(() => import("./pages/Progress"));
const HealthPage = lazy(() => import("./pages/Health"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const WelcomePage = lazy(() => import("./pages/Welcome"));

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // no answer (status 0) or a server error: try once more after a second, so a blip passes unseen but a
        // real failure is said within ≈ 2 s instead of after a long skeleton (BR-131); never a refused request
        // or an old app version
        retry: (n, e) =>
          !(e instanceof ApiSchemaError) &&
          !(e instanceof ApiError && (e.timedOut || (e.status > 0 && e.status < 500))) &&
          n < 1,
        retryDelay: 1000,
        refetchOnWindowFocus: true,
        // run even when the browser thinks it is offline: a request then fails with "No connection" and the page
        // shows it with Retry, instead of pausing with no data and no error (blank pages, G3 H1)
        networkMode: "always",
      },
      mutations: { networkMode: "always" }, // a save offline says so instead of waiting for ever (G3 H2)
    },
  });
}

const qc = makeQueryClient();

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <BrowserRouter>
          <ErrorBoundary where="app">
            <TokenGate>
              <TermsGate>
                <Suspense fallback={<Skeleton height={300} />}>
                  <Routes>
                    <Route path="welcome" element={<WelcomePage />} />
                    <Route element={<Layout />}>
                      <Route index element={<TodayPage />} />
                      <Route path="plan" element={<PlanPage />} />
                      <Route path="activities" element={<ActivitiesPage />} />
                      <Route path="activities/:id" element={<ActivityDetailPage />} />
                      <Route path="progress" element={<ProgressPage />} />
                      <Route path="trends" element={<Navigate to="/progress" replace />} />
                      <Route path="health" element={<HealthPage />} />
                      <Route path="settings" element={<SettingsPage />} />
                      <Route path="*" element={<TodayPage />} />
                    </Route>
                  </Routes>
                </Suspense>
              </TermsGate>
            </TokenGate>
          </ErrorBoundary>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
